import moment from "moment";
import { useEffect, useMemo } from "react";

/**
 * Normaliza o valor de um campo do tipo Tempo para "HH:mm".
 * Aceita moment, timestamp em ms (dateFormatter="number" do ProTable),
 * string de epoch legada persistida ou "HH:mm"/"HH:mm:ss".
 */
export const normalizeTimeFilterValue = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (moment.isMoment(value)) return value.isValid() ? value.format('HH:mm') : null;
    if (typeof value === 'number') {
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('HH:mm') : null;
    }
    const str = String(value).trim();
    if (/^\d{10,}$/.test(str)) {
        const parsed = moment(Number(str));
        return parsed.isValid() ? parsed.format('HH:mm') : null;
    }
    const parsed = moment(str, ['HH:mm', 'HH:mm:ss'], true);
    return parsed.isValid() ? parsed.format('HH:mm') : (str || null);
};


/**
 * Custom hook para criar filtros persistentes entre sessões / querystring / localStorage.
 *
 * - Reads values ​​from the URL (querystring) and from localStorage.
 * - Converts strings to moment() when necessary.
 * - Update URL using navigate()
 *
 * @param {FilterConfig[]} config - Array containing the definition of each filter (see typedef FilterConfig)..
 * @param {React.RefObject} actionRef - Ref used by ProTable.
 * @param {React.RefObject} formRef - Ref used by form (ProTable formRef) setFieldsValue / submit.
 * @param {object} location - Object return for useLocation() (react-router).
 * @param {object} navigate - Object return for useNavigate() (react-router).
 * @param {string} parentId - Name of father form
 * @param {object} columnIsOk - Boolean to define if form columns is ok
 * @param {string} id - String to get id de form 
 * @param {boolean} isRegistryForm - Boolean to check if is records form   
 *
 * @returns {{
 *   initialValues: Object,
 *   onSubmitFilters: function(Object):void,
 *   onReset: function():void
 * }}
 *
 * @example
 * const config = [
 *   { field: 'name', type: 'text', queryParam: 'name', localKey: 'pf_name' },
 *   { field: 'price', type: 'number', queryParam: 'price', localKey: 'pf_price' },
 *   { field: 'createdAt', type: 'dateRange', queryParamStart: 'created_at_start', queryParamEnd: 'created_at_end', localKeyStart: 'pf_c_start', localKeyEnd: 'pf_c_end' },
 *   { field: 'category', type: 'select', queryParam: 'category', localKey: 'pf_category'}
 * ];
 *
 * const { initialValues, onSubmitFilters, onReset } = usePersistentFilters(config, actionRef, formRef, location, navigate, parentId, true, id, false);
 * const { initialValues, onSubmitFilters, onReset } = usePersistentFilters(config, actionRef, formRef, location, navigate);
 * 
 */
export const usePersistentFilters = (config, actionRef, formRef, location, navigate, parentId = null, columnIsOk = true, id = null, isRegistryForm = false) => {

    // Read a filter 
    const readFilter = (filterConfig) => {

        if (parentId && location.pathname && !location.pathname.includes(parentId)) {
            return filterConfig.type === 'array' ? [] : null;
        }

        const urlParams = new URLSearchParams(location.search);
        let rawValue;

        // 1. para fields que foram inputados como dateRange
        if (filterConfig.type === 'dateRange') {
            const start = urlParams.get(filterConfig.queryParamStart) ?? localStorage.getItem(filterConfig.localKeyStart);
            const end = urlParams.get(filterConfig.queryParamEnd) ?? localStorage.getItem(filterConfig.localKeyEnd);
            if (start || end) {
                return [
                    start ? moment(start) : null,
                    end ? moment(end) : null,
                ];
            }
            // fallback vírgula
            rawValue = urlParams.get(filterConfig.queryParam) ?? localStorage.getItem(filterConfig.localKey);
            if (rawValue) {
                const [s, e] = rawValue.split(',');
                return [
                    s ? moment(Number(s) || s) : null,
                    e ? moment(Number(e) || e) : null,
                ];
            }
            return [null, null];
        }

        // 2. Pega valor
        rawValue = urlParams.get(filterConfig.queryParam) ?? localStorage.getItem(filterConfig.localKey);
        if (!rawValue) return filterConfig.type === 'array' ? [] : null;

        // 3. Se for array → split e retorna array
        if (filterConfig.type === 'array') {
            return rawValue
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        }

        // 4. Campo do tipo Tempo → devolve moment para o TimePicker
        if (filterConfig.type === 'time') {
            const timeStr = normalizeTimeFilterValue(rawValue);
            const parsed = timeStr ? moment(timeStr, 'HH:mm', true) : null;
            return parsed && parsed.isValid() ? parsed : null;
        }

        // 5. Outros: text, number, etc.
        return rawValue;
    };

    // Read all filters
    const readFilters = () => {
        const filters = {};
        if (parentId && location?.pathname && !location.pathname.includes(parentId)) {
            return filters;
        }

        config.forEach(filterConfig => {
            const value = readFilter(filterConfig);
            if (value != null && (!Array.isArray(value) || value.length > 0)) {
                filters[filterConfig.field] = value;
            }
        });
        return filters;
    }

    // save filter into local storage and url params
    const saveFilter = (urlParams, filterConfig, value) => {
        const safeValue = value !== undefined ? value : null;
        if (filterConfig.type === 'dateRange') {
            const [start, end] = Array.isArray(safeValue) ? safeValue : [null, null];
            if (start?.isValid()) {
                const str = start.format('YYYY-MM-DD');
                urlParams.set(filterConfig.queryParamStart, str);
                localStorage.setItem(filterConfig.localKeyStart, str);
            } else {
                urlParams.delete(filterConfig.queryParamStart);
                localStorage.removeItem(filterConfig.localKeyStart);
            }
            if (end?.isValid()) {
                const str = end.format('YYYY-MM-DD');
                urlParams.set(filterConfig.queryParamEnd, str);
                localStorage.setItem(filterConfig.localKeyEnd, str);
            } else {
                urlParams.delete(filterConfig.queryParamEnd);
                localStorage.removeItem(filterConfig.localKeyEnd);
            }
            urlParams.delete(filterConfig.queryParam);
            localStorage.removeItem(filterConfig.localKey);
            return;
        }

        // para tipos que foram inputados como array 
        if (filterConfig.type === 'array' && Array.isArray(safeValue)) {
            const str = safeValue
                .filter(v => v != null && v !== '')
                .map(v => String(v).trim())
                .join(',');

            if (str) {
                urlParams.set(filterConfig.queryParam, str);
                localStorage.setItem(filterConfig.localKey, str);
            } else {
                urlParams.delete(filterConfig.queryParam);
                localStorage.removeItem(filterConfig.localKey);
            }
            return;
        }


        // Campo do tipo Tempo: converte timestamp/moment para "HH:mm" antes
        // de persistir, mantendo o valor exatamente como digitado pelo usuário
        if (filterConfig.type === 'time') {
            const timeStr = normalizeTimeFilterValue(safeValue);
            if (timeStr) {
                urlParams.set(filterConfig.queryParam, timeStr);
                localStorage.setItem(filterConfig.localKey, timeStr);
            } else {
                urlParams.delete(filterConfig.queryParam);
                localStorage.removeItem(filterConfig.localKey);
            }
            return;
        }

        const finalValue = safeValue != null ? String(safeValue).trim() : null;
        if (finalValue && finalValue !== '') {
            urlParams.set(filterConfig.queryParam, finalValue);
            localStorage.setItem(filterConfig.localKey, finalValue);
        } else {
            urlParams.delete(filterConfig.queryParam);
            localStorage.removeItem(filterConfig.localKey);
        }
    };

    // clear filters is null
    const cleanFiltersIsNull = (values) => {
        const cleaned = {};

        Object.entries(values).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (typeof value === 'string' && value.trim() === '') return;
            if (Array.isArray(value) && value.every(v => v === null || v === undefined || v === '')) return;
            cleaned[key] = value;
        });

        return cleaned;
    }

    // Save all filters
    const saveFilters = (values) => {
        const urlParams = new URLSearchParams(location.search);
        config.forEach((filterConfig) => {
            const fieldValue = values[filterConfig.field];
            saveFilter(urlParams, filterConfig, fieldValue);
        });
        navigate({
            pathname: location.pathname,
            search: urlParams.toString(),
        }, { replace: true });
    };

    // convert data string 
    const convertReadableDateString = (str) => {
        const date = new Date(str);

        // verifica se é realmente uma data válida
        if (!isNaN(date.getTime())) {
            return date.getTime(); // timestamp em ms
        }

        return str;
    }

    // check type is date, and split to start and end 
    const checkIfHasDateToOneField = (obj, isMoment) => {
        Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (typeof value === 'string' && value.includes(',')) {
                const [startStr, endStr] = value.split(',').map(s => convertReadableDateString(s));

                let start = Number(startStr);
                let end = Number(endStr);
                if (isMoment) {
                    start = moment(Number(startStr));
                    end = moment(Number(endStr));
                }
                obj[key] = [start, end];
            }
        });

        return obj;
    }

    // when the form submit filter
    const onSubmitFilters = (values) => {
        saveFilters(values);
    };

    // when the form submit clear 
    const clearFilters = () => {
        const urlParams = new URLSearchParams();
        // Remove all (query params and localStorage)
        config.forEach((filterConfig) => {
            if (filterConfig.type === 'dateRange') {
                urlParams.delete(filterConfig.queryParamStart);
                urlParams.delete(filterConfig.queryParamEnd);
                localStorage.removeItem(filterConfig.localKeyStart);
                localStorage.removeItem(filterConfig.localKeyEnd);
            } else {
                urlParams.delete(filterConfig.queryParam);
                localStorage.removeItem(filterConfig.localKey);
            }
        });

        navigate({
            pathname: location.pathname,
            search: '',
        }, { replace: true });

        // clear form
        if (formRef.current) {
            formRef.current.resetFields();
        }

        // reload clear table
        if (actionRef.current?.reloadAndRest) {
            actionRef.current.reloadAndRest();
        } else if (actionRef.current?.reload) {
            actionRef.current.reload({ current: 1 });
        }
    };

    const currentFormPath = location.pathname;
    const initialValues = useMemo(() => {
        if (!parentId || !currentFormPath.includes(parentId) || config.length === 0) {
            return {};
        }
        // return readFilters();
    }, [config, parentId, currentFormPath, location.search]);

    useEffect(() => {
        // Caso seja o form de registro, não realiza a requisição, a filtragem, pois, a requisição esta sendo efetuada diretamente na req
        if (isRegistryForm) return;

        // Caso os dados ainda não estejam rendenizados, só sai da função
        if (!columnIsOk) return;

        // Logica para verificar se o filtro sera para o form clicado em registro
        if (parentId && !currentFormPath.includes(parentId)) return;

        // 1. Busca se há filtros estabelecidos via query params ou localstorage
        const filters = readFilters();

        // 2. Pega apenas os valores que nao são sao null para inputar no formulario
        let filtersClean = cleanFiltersIsNull(filters);

        // 3. caso haja um campo unico, que leva em consideração DATA, como um array timestamp [x, y], converte o mesmo para moment, para inputar no field
        filtersClean = checkIfHasDateToOneField(filtersClean, true);

        // 4. Seta os valores no formulario
        if (formRef.current) {
            formRef.current.setFieldsValue(filtersClean);
        }

        // 5. Instancia url params
        const urlParamsToSync = new URLSearchParams();

        // 6. Seta a url com os parametros de acordo com o tipo
        config.forEach((filterConfig) => {
            const value = filtersClean[filterConfig.field];
            if (filterConfig.type === 'dateRange') {
                const [start, end] = value || [null, null];
                if (start) urlParamsToSync.set(filterConfig.queryParamStart, start.format('YYYY-MM-DD'));
                if (end) urlParamsToSync.set(filterConfig.queryParamEnd, end.format('YYYY-MM-DD'));

            } else if (filterConfig.type === 'time' && value != null && value !== '') {
                const timeStr = normalizeTimeFilterValue(value);
                if (timeStr) urlParamsToSync.set(filterConfig.queryParam, timeStr);

            } else if (value != null && value !== '') {
                if (Object.keys(filtersClean).length > 0) {

                    // Caso seja um obj, que foi convertido em moment dentro de checkIfHasDateToOneField, converte para timestamp para url 
                    Object.keys(filtersClean).forEach(key => {
                        const value = filtersClean[key];
                        if (typeof value === 'string' && value.includes(',')) {
                            const [startStr, endStr] = value.split(',').map(s => convertReadableDateString(s));
                            filtersClean[key] = [startStr, endStr];
                            urlParamsToSync.set(filterConfig.queryParam, value.toString());
                        }
                    });
                } else {
                    urlParamsToSync.set(filterConfig.queryParam, value.toString());
                }
            }
        });

        // 7. Pega os parametros de busca da url e verifica se é diferente, para atualizar a mesma
        const newSearch = urlParamsToSync.toString();
        const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
        if (newSearch !== currentSearch) {
            navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
        }

        // 9. atualiza a tabela
        formRef.current?.submit();
    }, [navigate, parentId, actionRef, formRef, columnIsOk, config]);

    return { initialValues, onSubmitFilters, onReset: clearFilters };
}